import { type JSX, type MouseEvent } from 'react';
import { useScrollState } from '../../../hooks/useScrollState';
import type { StrateNavigationProps } from './StrateNavigation.types';
import { StrateNavigationButton } from './StrateNavigationButton';
import type { StrateNavigationButtonDirection } from './StrateNavigationButton.types';

/**
 * StrateNavigation wraps the Strate's list and provides horizontal navigation controls.
 *
 * Renders navigation buttons on both sides of a Strate that:
 * - Automatically show/hide based on the visibility of the first and last items
 * - Have snapping behavior when clicked:
 *   - Left button: Scrolls to the nearest item whose left edge is not visible in the viewport
 *   - Right button: Scrolls to the nearest item whose right edge is not visible in the viewport
 *   - Maintains spacing on the left of the viewport equal to the computed padding-left of the list
 */
export function StrateNavigation({
  ariaLabelNavButtonLeft = 'Previous',
  ariaLabelNavButtonRight = 'Next',
  scrollBehavior = 'smooth',
  verticalAlign = 'middle',
  listRef,
  children,
  ...buttonProps
}: StrateNavigationProps): JSX.Element {
  const stratePaddingLeft = listRef?.current
    ? parseInt(window?.getComputedStyle(listRef.current).paddingLeft, 0)
    : 0;
  const { isAtStart, isAtEnd } = useScrollState(
    listRef,
    'horizontal',
    stratePaddingLeft,
  );

  const strateChildren = Array.from(
    listRef?.current?.children ?? [],
  ) as HTMLElement[];

  const handleNavigationClick = (
    e: MouseEvent,
    direction: StrateNavigationButtonDirection,
  ) => {
    e.stopPropagation();

    if (!listRef?.current) {
      return;
    }

    let itemToScrollTo: HTMLElement | undefined;
    const strateWidth = listRef.current.offsetWidth;

    if (direction === 'left') {
      itemToScrollTo = strateChildren.find((child) => {
        return (
          // Find the closest child whose left edge is not visible in the viewport
          child.getBoundingClientRect().left + strateWidth >=
          (listRef.current?.getBoundingClientRect().left ?? 0)
        );
      });
    } else {
      itemToScrollTo = strateChildren.find(
        (child) =>
          // Find the closest child whose right edge is not visible in the viewport
          child.getBoundingClientRect().right >
          (listRef.current?.getBoundingClientRect().right ?? 0),
      );
    }

    if (itemToScrollTo && listRef.current) {
      const scrollToPosition = itemToScrollTo.offsetLeft - stratePaddingLeft;
      listRef.current.scrollTo({
        left: scrollToPosition,
        behavior: scrollBehavior,
      });
    }
  };

  return (
    <>
      <StrateNavigationButton
        verticalAlign={verticalAlign}
        direction="left"
        isHidden={isAtStart}
        aria-label={ariaLabelNavButtonLeft}
        onClick={(e) => handleNavigationClick(e, 'left')}
        {...buttonProps}
      />
      {children}
      <StrateNavigationButton
        verticalAlign={verticalAlign}
        direction="right"
        isHidden={isAtEnd}
        aria-label={ariaLabelNavButtonRight}
        onClick={(e) => handleNavigationClick(e, 'right')}
        {...buttonProps}
      />
    </>
  );
}
